/**
 * Cross-app URL resolution shared by all frontends (admin / live / public).
 *
 * Resolution order for a target app's base URL:
 *  1. Explicit env override — VITE_ADMIN_URL / VITE_LIVE_URL (or legacy
 *     VITE_AUDIENCE_URL) / VITE_PUBLIC_URL. Highest priority, lets any build
 *     pin an exact URL.
 *  2. Derive from the CURRENT hostname when it's a showliner.io domain. The
 *     deployment layout is regular —
 *         admin = "admin." + base    live = "live." + base    public = base
 *     where `base` is the public host (current host with any `admin.`/`live.`
 *     prefix stripped). So admin.dev.showliner.io → base `dev.showliner.io` →
 *     live.dev.showliner.io / dev.showliner.io, and the same rule yields the
 *     prod hosts from *.showliner.io. This is why dev "just works" with zero
 *     per-project cross-app env vars (and any future env under *.showliner.io
 *     does too).
 *  3. Fallback — localhost dev ports (when running `vite dev`), else the prod
 *     defaults (covers *.pages.dev previews and anything off-pattern).
 */

type App = 'admin' | 'live' | 'public';

const DEV_PORT: Record<App, string> = {
    admin: 'http://localhost:3002',
    live: 'http://localhost:3001',
    public: 'http://localhost:3003',
};

const PROD_DEFAULT: Record<App, string> = {
    admin: 'https://admin.showliner.io',
    live: 'https://live.showliner.io',
    public: 'https://showliner.io',
};

function envOverride(app: App): string | undefined {
    const env = import.meta.env;
    if (app === 'admin') return env.VITE_ADMIN_URL;
    if (app === 'public') return env.VITE_PUBLIC_URL;
    // live (audience) — accept the legacy VITE_AUDIENCE_URL alias too.
    return env.VITE_LIVE_URL || env.VITE_AUDIENCE_URL;
}

function resolveBase(app: App): string {
    const override = envOverride(app);
    if (override) return override;

    if (typeof window !== 'undefined') {
        const { protocol, hostname } = window.location;
        if (hostname.endsWith('showliner.io')) {
            let base = hostname;
            if (hostname.startsWith('admin.')) base = hostname.slice('admin.'.length);
            else if (hostname.startsWith('live.')) base = hostname.slice('live.'.length);
            const host = app === 'public' ? base : `${app}.${base}`;
            return `${protocol}//${host}`;
        }
    }

    return import.meta.env.DEV ? DEV_PORT[app] : PROD_DEFAULT[app];
}

export function getAdminBaseUrl(): string {
    return resolveBase('admin');
}

export function getLiveBaseUrl(): string {
    return resolveBase('live');
}

export function getPublicBaseUrl(): string {
    return resolveBase('public');
}
